// Frozen chart-theme constants for the dashboard ECharts engine.
// Single source of truth for palette / text / structural styling so the pure
// option builders in echartsOptions.js carry no hardcoded hex or magic numbers.
// Consumed by dashboard-widget.vue echarts branch (see .claude/plans/DASHBOARD_ECHARTS_POC.md).
//
// Pattern mirrors src/features/Tags/components/monitoring/cmlColoringConstants.js:
// Object.freeze'd, comment-cited to its token source, values duplicated from
// _design-tokens.scss (per D7 — no runtime CSS-var reads exist in the app).

// Categorical palette — 8-color brand order. Each hex is copied verbatim from
// the design token cited beside it (src/styles/_design-tokens.scss). Gradient
// tokens contribute their start color (the 0% stop).
export const CHART_PALETTE = Object.freeze([
  '#03624c', // 1 castleton   — --ds-brand-castleton (_design-tokens.scss:106)
  '#2cc295', // 2 emerald     — --ds-brand-emerald (_design-tokens.scss:104)
  '#547D9A', // 3 info        — --ds-gradient-info 0% stop (_design-tokens.scss:171)
  '#d97706', // 4 pending     — --ds-color-pending (_design-tokens.scss:96)
  '#665B9B', // 5 purple      — --ds-gradient-purple 0% stop (_design-tokens.scss:174)
  '#0d9488', // 6 harvest     — --ds-color-harvest (_design-tokens.scss:94)
  '#9ca3af', // 7 neutral     — --ds-color-neutral (_design-tokens.scss:98)
  '#b71c1c', // 8 error       — --ds-gradient-error 0% stop (_design-tokens.scss:173)
]);

// A value-STABLE categorical colour: a value's colour is its RANK in the sorted set of the
// dimension's distinct values, mapped into CHART_PALETTE. Rank (not a hash) keeps colours DISTINCT
// up to the palette size (no two categories share a hue until there are >8), and is identical
// wherever the same value set is used — so the chart fill and the 3D isolate match when both pass
// the same `allValues`. A missing/absent set falls back to a deterministic hash (may repeat a hue).
export function stableCategoryColor(value, allValues) {
  const key = String(value == null ? '' : value);
  if (Array.isArray(allValues) && allValues.length) {
    const sorted = [...new Set(allValues.map((v) => String(v == null ? '' : v)))].sort();
    const idx = sorted.indexOf(key);
    if (idx >= 0) return CHART_PALETTE[idx % CHART_PALETTE.length];
  }
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) | 0;
  return CHART_PALETTE[Math.abs(h) % CHART_PALETTE.length];
}

// Sequential value ramp for heatmap cell fills, low → high = neutral → severity. The heatmap
// builder's continuous visualMap interpolates between these stops (never per-datum itemStyle — the
// coloring contract). Flat fills only (no gradients or shadows on cells; severity lives in fill).
// Gold / pending-orange / error hexes are the app's severity values (duplicated per the D7 pattern);
// the low stop is a light neutral tint (light) or a muted dark surface (dark).
export const CHART_HEATMAP_RAMP = Object.freeze({
  light: Object.freeze(['#eef5f2', '#d2a000', '#d97706', '#b71c1c']),
  dark: Object.freeze(['#2a3f39', '#a07a00', '#c2410c', '#ef5350']),
});

// Text styling for axis labels, tooltips, and legend.
export const CHART_TEXT = Object.freeze({
  // Muted label color for axes / legend text.
  MUTED: '#6b7280', // --ds-color-neutral-hover (_design-tokens.scss:99)
  // Strong label color for emphasis text (e.g. the showcase donut center total).
  STRONG: '#1f2937', // matches the showcase --text (dashboard-manager.html :19)
  FONT_FAMILY: "'IBM Plex Sans', 'Roboto', sans-serif", // --ds-font-family-body (_design-tokens.scss:19)

  // Unified inner-text ramp (px) for the widget-size level, keyed off the legacy fontSize
  // (0 small / 1 normal / 2 large; absent or out-of-range = normal). Per user direction the chart
  // text ramp is 13 / 14 / 16: FONT_SIZE_PX (general + legend, via resolveFontSizePx),
  // INNER_AXIS_LABEL_PX (axis tick labels, bar/column value labels, heatmap visualMap scale,
  // showcase pie/donut legend) and INNER_DONUT_LABEL_PX (donut center sub-label) all share
  // 13 / 14 / 16; INNER_DONUT_TOTAL_PX (donut center number) is 22 / 26 / 30. The normal tier raises
  // the axis labels from the pre-tier fixed AXIS_LABEL_SIZE_PX (11) to 14; the widget-size identity
  // waiver covers all chart text at all tiers, so the old deep-equal baselines are retired.
  FONT_SIZE_PX: Object.freeze({ 0: 13, 1: 14, 2: 16 }),

  // Safe default (normal) for undefined / out-of-range fontSize (the widget computed returns
  // undefined for such values, and charts must never inherit it).
  FONT_SIZE_DEFAULT_PX: 14,

  INNER_AXIS_LABEL_PX: Object.freeze({ 0: 13, 1: 14, 2: 16 }),
  INNER_DONUT_TOTAL_PX: Object.freeze({ 0: 22, 1: 26, 2: 30 }),
  INNER_DONUT_LABEL_PX: Object.freeze({ 0: 13, 1: 14, 2: 16 }),
});

// Structural (non-data) styling: grid lines, axes, legend, grid insets, bars.
export const CHART_STRUCTURE = Object.freeze({
  GRID_LINE_COLOR: 'rgba(0, 0, 0, 0.08)', // --ds-border-subtle, light theme (_design-tokens.scss:161)
  AXIS_LINE_COLOR: 'rgba(0, 0, 0, 0.12)', // --ds-border-default, light theme (_design-tokens.scss:162)
  AXIS_LABEL_SIZE_PX: 11, // one step below --ds-font-caption 12px (_design-tokens.scss:80)

  // Cartesian grid insets (px) — compact for dashboard tiles.
  GRID: Object.freeze({ top: 24, right: 16, bottom: 24, left: 16 }),

  // Showcase (flag-on) grid overrides, merged onto GRID to pixel-match
  // dashboard-manager.html. Horizontal bars adopt c-types' full compact inset so bars
  // fill the tile and right-positioned value labels clear the plot — containLabel
  // reserves axis-label space only, not series labels (c-types grid, :674). Columns
  // adopt c-equip's bottom/left insets (:702); their top/right already match GRID.
  GRID_SHOWCASE_BAR: Object.freeze({ top: 8, right: 28, bottom: 8, left: 12 }),
  GRID_SHOWCASE_COLUMN: Object.freeze({ bottom: 26, left: 12 }),

  // Legend defaults; position overridable per widget via ctx.legend.
  LEGEND: Object.freeze({
    DEFAULT_POSITION: 'bottom',
    ITEM_GAP: 12,
    ICON: 'roundRect',
  }),

  // Bar / column styling.
  BAR: Object.freeze({
    BORDER_RADIUS: 3, // slight rounding on the value end (~--ds-radius-sm 4px scale)
    MAX_WIDTH: 32, // default (flag-off) cap for both orientations
    // Showcase (flag-on) caps — thinner horizontal bars, chunkier columns, matching
    // dashboard-manager.html (c-types barMaxWidth 22, c-equip barMaxWidth 40).
    SHOWCASE_MAX_WIDTH_BAR: 22,
    SHOWCASE_MAX_WIDTH_COLUMN: 40,
    // Uniform fill for showcase horizontal bars whose category is NOT a severity level:
    // brand castleton (= CHART_PALETTE[0]); the showcase names it CAST.
    SINGLE_COLOR: '#03624c', // --ds-brand-castleton (_design-tokens.scss:106)
  }),

  // Selection styling for series select.itemStyle. A rich-black border + soft
  // shadow that reads on EVERY palette color — a palette-colored accent is
  // invisible when the selected datum already uses that same hue.
  SELECT_ACCENT: '#021a1a', // --ds-brand-rich-black (_design-tokens.scss:107)
  SELECT_SHADOW_BLUR: 6,
  SELECT_SHADOW_COLOR: 'rgba(2, 26, 26, 0.35)', // --ds-brand-rich-black @ 35%

  // Emphasis styling for series emphasis.itemStyle — drives BOTH hover and the
  // programmatic highlight() dispatch (same channel). Slightly stronger than select
  // so a dispatched highlight reads, but moderate so hover isn't heavier than Apex.
  EMPHASIS_SHADOW_BLUR: 8,
  EMPHASIS_SHADOW_COLOR: 'rgba(2, 26, 26, 0.45)', // --ds-brand-rich-black @ 45%
});
